import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL as API } from '../config/api';
import {
  ChevronRight, X, Mic, Share2, Swords, Music, Plus,
  TrendingUp, Scissors, Radio, ExternalLink, Heart,
  Bot, Shuffle, UserPlus, Layers, Headphones, Loader2
} from 'lucide-react';

const ICON_MAP = {
  mic: Mic, share: Share2, swords: Swords, music: Music,
  plus: Plus, "trending-up": TrendingUp, scissors: Scissors,
  radio: Radio, "external-link": ExternalLink, heart: Heart,
  bot: Bot, shuffle: Shuffle, "user-plus": UserPlus,
  layers: Layers, headphones: Headphones,
};

const PRIORITY_COLORS = {
  high: 'bg-[#007AFF] hover:bg-[#0066DD] text-white',
  medium: 'bg-white/10 hover:bg-white/15 text-white border border-white/10',
  low: 'bg-transparent hover:bg-white/5 text-zinc-400 border border-white/5',
};

export function NextActionBar({ context = "general", className = "" }) {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchActions = async () => {
      try {
        const res = await fetch(`${API}/api/content-loop/next-actions?context=${context}`, {
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setActions(data.actions || []);
        }
      } catch (err) {
        console.error('NextAction fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchActions();
  }, [isAuthenticated, getAuthHeaders, context]);

  if (dismissed || !isAuthenticated || loading || actions.length === 0) return null;

  return (
    <div className={`bg-[#141414] border border-white/10 rounded-xl p-4 ${className}`} data-testid="next-action-bar">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">What's Next</p>
        <Button variant="ghost" size="icon" className="h-6 w-6 text-zinc-500" onClick={() => setDismissed(true)}>
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
      <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 -mx-1 px-1 scrollbar-hide">
        {actions.map((action) => {
          const Icon = ICON_MAP[action.icon] || ChevronRight;
          return (
            <Button
              key={action.id}
              onClick={() => action.link && navigate(action.link)}
              className={`${PRIORITY_COLORS[action.priority] || PRIORITY_COLORS.medium} rounded-lg text-xs px-3 py-2 h-auto flex items-center gap-2 whitespace-nowrap snap-start shrink-0 transition-all duration-200`}
              data-testid={`next-action-${action.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {action.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

export function DailyGoalsWidget({ className = "" }) {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [goals, setGoals] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchGoals = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await fetch(`${API}/api/content-loop/goals`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setGoals(await res.json());
      }
    } catch (err) {
      console.error('Goals fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  if (!isAuthenticated || loading || !goals) return null;

  const GOAL_ICONS = {
    music: Music, swords: Swords, heart: Heart,
    "message-circle": Mic, headphones: Headphones,
    upload: Plus, users: UserPlus, trophy: Swords,
  };

  return (
    <div className={`bg-[#141414] border border-white/10 rounded-xl p-4 ${className}`} data-testid="daily-goals-widget">
      {/* Streak + XP Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">Daily Goals</p>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            {goals.summary.daily_completed}/{goals.summary.daily_total} completed
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-lg font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {goals.streak.current}
            </p>
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">streak</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-[#FBBF24]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {goals.streak.total_xp}
            </p>
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">XP</p>
          </div>
        </div>
      </div>

      {/* Overall progress */}
      <div className="w-full h-2 rounded-full bg-white/10 mb-4 overflow-hidden">
        <div
          className="h-full rounded-full bg-[#007AFF] transition-all duration-500"
          style={{ width: `${goals.summary.daily_percentage}%` }}
        />
      </div>

      {/* Goal Items */}
      <div className="space-y-2.5">
        {goals.daily_goals.map((g) => {
          const GIcon = GOAL_ICONS[g.icon] || Music;
          return (
            <div key={g.goal_id} className="flex items-center gap-3" data-testid={`goal-${g.goal_id}`}>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${g.completed ? 'bg-[#34D399]/15' : 'bg-white/5'}`}>
                <GIcon className={`w-3.5 h-3.5 ${g.completed ? 'text-[#34D399]' : 'text-zinc-500'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-xs ${g.completed ? 'text-[#34D399] line-through' : 'text-zinc-300'}`}>{g.label}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${g.completed ? 'bg-[#34D399]' : 'bg-[#007AFF]'}`}
                      style={{ width: `${g.percentage}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-zinc-500 w-8 text-right">{g.current}/{g.target}</span>
                </div>
              </div>
              <span className="text-[10px] text-[#FBBF24] font-semibold">+{g.xp}xp</span>
            </div>
          );
        })}
      </div>

      {/* Weekly Goals Preview */}
      {goals.weekly_goals?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">Weekly</p>
          <div className="space-y-1.5">
            {goals.weekly_goals.map((g) => (
              <div key={g.goal_id} className="flex items-center justify-between text-xs">
                <span className={g.completed ? 'text-[#34D399] line-through' : 'text-zinc-400'}>{g.label}</span>
                <span className="text-zinc-500">{g.current}/{g.target}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function RecycleSuggestions({ className = "" }) {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/api/content-loop/recycle-suggestions`, {
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.suggestions || []);
        }
      } catch (err) {
        console.error('Recycle suggestions error:', err);
      }
    };
    fetch_();
  }, [isAuthenticated, getAuthHeaders]);

  if (!isAuthenticated || suggestions.length === 0) return null;

  return (
    <div className={`bg-[#141414] border border-white/10 rounded-xl p-4 ${className}`} data-testid="recycle-suggestions">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-3">Resurface Your Content</p>
      <div className="space-y-2.5">
        {suggestions.map((s, i) => (
          <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.03] cursor-pointer transition-colors"
            onClick={() => s.action?.link && navigate(s.action.link)}>
            <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0 overflow-hidden">
              {s.cover ? <img src={s.cover} alt="" className="w-full h-full object-cover" /> : <Music className="w-4 h-4 text-zinc-500" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{s.title}</p>
              <p className="text-[10px] text-zinc-500">{s.message}</p>
            </div>
            {s.action && (
              <Button size="sm" variant="ghost" className="text-[10px] text-[#007AFF] h-6 px-2 hover:bg-[#007AFF]/10">
                {s.action.label}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
